import { Component, ViewChild } from '@angular/core';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { CommonModule } from '@angular/common';
import { TextboxComponent } from '../../../design-system/textbox/textbox.component';
import { TextboxConfig } from '../../../design-system/textbox/textbox.modal';
import { TwoDigitDecimalNumberDirective } from 'src/app/_directives/twoDigitDecimalNumber.directive';
import { MoneyDecimalNumberDirective } from 'src/app/_directives/moneyDecimalNumber.directive';
import { TranslateModule } from '@ngx-translate/core';
import { formatAmountInputWithSeparators, parseAmount } from 'src/app/_helpers/number-format.utils';
import { stripSeparatorsPreservingCaret } from 'src/app/_helpers/money-input-caret.utils';

@Component({
  selector: 'category-input-renderer',
  templateUrl: './category-input-renderer.component.html',
  standalone: true,
  imports: [CommonModule, TextboxComponent, TwoDigitDecimalNumberDirective, MoneyDecimalNumberDirective, TranslateModule],
})
export class CategoryInputRendererComponent implements ICellRendererAngularComp {
  // textboxConfig: TextboxConfig = { id: '', size: 'sm' };
  textboxConfig: TextboxConfig = {
    id: '',
    label: '',
    placeholder: '',
    currencyText: null,
    disabled: true,
    required: false,
    size: 'sm',
    rightSideText: null,
    helperText: null,
  };

  @ViewChild(TextboxComponent) textbox?: TextboxComponent;

  params: any;
  private fieldKey: string = '';
  private isEditing = false;
  private onValueChange: ((rowData: any, fieldKey: string, value: string) => void) | null = null;

  agInit(params: any): void {
    // A freshly created cell is never mid-edit; guards against a pooled instance
    // starting life with a stale flag and skipping its first format.
    this.isEditing = false;
    this.initialize(params);
  }

  refresh(params: any): boolean {
    this.initialize(params);
    return true;
  }

  private initialize(params: any): void {
    this.params = params;
    this.fieldKey = params.colDef?.cellRendererParams?.fieldKey ?? '';
    this.onValueChange = params.colDef?.cellRendererParams?.onValueChange ?? null;
    if (params.colDef?.cellRendererParams) {
      this.textboxConfig.label = params.colDef?.cellRendererParams.label;
      this.textboxConfig.disabled = params.colDef?.cellRendererParams.disabled;
      this.textboxConfig.required = params.colDef?.cellRendererParams.required;
      this.textboxConfig.size = params.colDef?.cellRendererParams.size ?? 'sm';
      this.textboxConfig.readonly = params.colDef?.cellRendererParams.readonly;
      this.textboxConfig.maxLength = params.colDef?.cellRendererParams.maxLength;
      this.textboxConfig.maxLengthCount = params.colDef?.cellRendererParams.maxLengthCount;
      this.textboxConfig.rightSideText = params.colDef?.cellRendererParams.rightSideText;
      this.textboxConfig.rightSideTextClass = params.colDef?.cellRendererParams.rightSideTextClass;
      this.textboxConfig.currencyText = params.colDef?.cellRendererParams.currencyText;
      this.textboxConfig.helperText = params.colDef?.cellRendererParams.helperText;
      this.textboxConfig.placeholder = params.colDef?.cellRendererParams.placeholder;
      this.textboxConfig.iconSrc = params.colDef?.cellRendererParams.iconSrc;
      this.textboxConfig.iconPosition = params.colDef?.cellRendererParams.iconPosition;

    }

    const rawValue = this.fieldKey && params.data ? params.data[this.fieldKey] : null;
    const formatAsAmount = params.colDef?.cellRendererParams?.formatAsAmount;
    const displayValue = formatAsAmount && !this.isEditing && rawValue != null && rawValue !== '' && !isNaN(Number(String(rawValue).replace(/,/g, '')))
      ? formatAmountInputWithSeparators(rawValue)
      : (rawValue != null && rawValue !== '' ? String(rawValue) : '');
    const cellError: string | null = (this.fieldKey && params.data ? params.data[this.fieldKey + 'Error'] : null) ?? null;

    this.textboxConfig = {
      ...this.textboxConfig,
      id: `category-input-${this.fieldKey}-${params.rowIndex}`,
      // Mid-edit the user's raw text is the source of truth. onGridValueChange
      // refreshes the edited column too, and rebuilding the text from the numeric
      // model here would drop trailing decimals ("800.00" → String(800) → "800")
      // or swallow a trailing dot while someone types "1200.".
      value: this.isEditing ? this.textboxConfig.value : displayValue,
      isInvalid: !!cellError,
      // helperText: cellError,
    };
  }

  /**
   * Money cells drop their thousands separators while focused so the user edits a
   * plain number — grouping is re-applied in onBlur(). Setting isEditing here (rather
   * than waiting for the first onChange) stops initialize()/refresh() re-formatting
   * the text under the user mid-edit.
   */
  onFocus(): void {
    if (!this.params?.colDef?.cellRendererParams?.formatAsAmount) { return; }
    this.isEditing = true;
    stripSeparatorsPreservingCaret(
      this.textbox?.inputElement?.nativeElement,
      (raw: string) => { this.textboxConfig = { ...this.textboxConfig, value: raw }; }
    );
  }

  onChange(event: any): void {
    this.isEditing = true;
    const raw: string = event ?? '';
    // const raw: string = event?.target?.value ?? '';
    // Strip separators before parsing — a re-focused money cell still holds "1,234.56".
    const stripped = String(raw).replace(/,/g, '');
    const value = stripped !== '' && !isNaN(Number(stripped)) ? parseAmount(raw).toFixed(2) : '';

    if (this.params?.data && this.fieldKey) {
      this.params.data[this.fieldKey] = value !== '' ? parseFloat(value) : null;
    }

    if (this.onValueChange && this.params?.data) {
      this.onValueChange(this.params.data, this.fieldKey, value);
    }
  }

  onBlur(): void {
    this.isEditing = false;
    const formatAsAmount = this.params?.colDef?.cellRendererParams?.formatAsAmount;
    if (formatAsAmount && this.params?.data && this.fieldKey) {
      this.textboxConfig = {
        ...this.textboxConfig,
        value: formatAmountInputWithSeparators(this.params.data[this.fieldKey]),
      };
    }
  }
}
